import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config';
import { useNavigate } from 'react-router-dom'; // Import useNavigate for navigation
import logo from './css/images.png';
import Lottie from 'lottie-react';
import loader from './css/loader.json';

const StaticIPs = () => {
  const [staticIPs, setStaticIPs] = useState({
    mainStaticIP: '', // Static IP from SystemAdmin
    additionalStaticIPs: [], // Static IPs from AdditionalUser
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate(); // Initialize navigate hook

  // Memoize handleIPClick to avoid unnecessary re-creations
  const handleIPClick = useCallback((companyName, userId) => {
    // Store the userId in localStorage
    localStorage.setItem('userId', userId);
    // Navigate to the /year-select page
    navigate('/year-select');
  }, [navigate]);

  // Fetch static IPs after component mounts
  useEffect(() => {
    const fetchStaticIPs = async () => {
      try {
        // Retrieve the access token from localStorage (assuming it was stored during login)
        const token = localStorage.getItem('accessToken');
        const userId = localStorage.getItem('userId'); // Assuming userId is also stored in localStorage

        if (!token) {
          throw new Error('No access token found. Please log in.');
        }

        if (!userId) {
          throw new Error('No user ID found. Please log in.');
        }

        // Make a GET request to fetch the static IPs for the given userId
        const response = await axios.get(`${API_BASE_URL}/user/static-ips/${userId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        // Set the static IPs in state based on the correct structure
        setStaticIPs({
          mainStaticIP: response.data.staticIP || '', // Static IP from SystemAdmin (fallback to empty string if not present)
          additionalStaticIPs: response.data.staticIPs || [], // Static IPs from AdditionalUser (fallback to empty array if not present)
        });

        setLoading(false);

        // Auto-navigate if there's only one additionalStaticIP
        if (response.data.staticIPs && response.data.staticIPs.length === 1) {
          handleIPClick(response.data.staticIPs[0].companyName, response.data.staticIPs[0].userId);
        }
      } catch (err) {
        // Handle 404 error by logging out the user
        if (err.response && err.response.status === 404) {
          // Clear the localStorage and redirect to the login page
          localStorage.removeItem('accessToken');
          localStorage.removeItem('userId');
          navigate('/'); // Redirect to login page
        } else {
          setError(err.message || 'Failed to fetch static IPs');
        }
        setLoading(false);
      }
    };

    fetchStaticIPs();
  }, [handleIPClick, navigate]); // Add navigate to dependency array

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-sm">
        <Lottie animationData={loader} loop={true} autoplay={true} style={{ height: 100, width: 100 }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl border border-red-100 text-center max-w-md w-full">
          <div className="text-red-500 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Error Occurred</h2>
          <p className="text-slate-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-xl font-semibold transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-[500px]">
        <div className="bg-white p-8 md:p-10 rounded-2xl shadow-xl border border-slate-100">
          <div className="flex justify-center mb-8">
            <img src={logo} alt="DeviSoft Logo" className="w-48 h-16 object-contain" />
          </div>

          <h1 className="text-2xl font-bold text-slate-800 mb-6 text-center">Select Company</h1>

          <div className="overflow-hidden rounded-xl border border-slate-200 shadow-sm">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-100 border-b border-slate-200">
                  <th className="px-6 py-4 text-sm font-bold text-slate-700 uppercase tracking-wider">
                    Available Companies
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {staticIPs.additionalStaticIPs.length > 0 ? (
                  staticIPs.additionalStaticIPs.map((ip, index) => (
                    <tr
                      key={index}
                      className="hover:bg-blue-50 transition-colors cursor-pointer group"
                      onClick={() => handleIPClick(ip.companyName, ip.userId)}
                    >
                      <td className="px-6 py-4 border-r border-slate-100 last:border-r-0">
                        <span className="text-blue-600 font-semibold group-hover:text-blue-700 transition-colors">
                          {ip.companyName}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="px-6 py-10 text-center text-slate-500 italic bg-slate-50">
                      No additional static IPs available.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-center mt-8 text-slate-500 text-sm">
          &copy; {new Date().getFullYear()} DeviSoft. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default StaticIPs;
